// @flow
import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { useUserInputAsTextField } from '@tectonics/prs/user-input-values';
import { InputMask } from '@tectonics/components/input-mask';
import { useUserInputValidationError } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useCurrentPaymentTypeOptionalFields } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';
import { getCountry, useIncomingPayment } from '@tectonics/prs/incoming-payment';
import { getCountryFromCode } from '@tectonics/commons/country';
import { useTranslation } from 'react-i18next';

import { TextField, Tooltip } from '../../../../atoms';
import { GenericValidationMessage } from './generic-validation-message';

export function FieldGenericPhoneNumber(): Node {
  const incomingPayment = useIncomingPayment();
  const countryCode = getCountry(incomingPayment);
  const country = getCountryFromCode(countryCode);

  const customerForm = country?.customerForm;
  const phoneNumber = customerForm?.phoneNumber;

  const { t } = useTranslation('opened-page');
  const inputId = useUniqueId();
  const submitting = useSubmitting();

  const [value, handleChange] = useUserInputAsTextField(phoneNumber?.inputFieldName || '');

  const validationError = useUserInputValidationError(phoneNumber?.inputFieldName || '');
  const formHasBeenSent = useFormHasBeenSent();

  const isOptionalFieldByPaymentType = useCurrentPaymentTypeOptionalFields('telephone');

  if (!customerForm || !phoneNumber || isOptionalFieldByPaymentType || !phoneNumber.inputFieldName || !phoneNumber.mask || !phoneNumber.labelKey || !phoneNumber.placeholderKey) {
    return null;
  }

  return (
    <div data-test-name="customer-phone-number">
      <label htmlFor={inputId}>{t(phoneNumber.labelKey || '')}</label>
      {phoneNumber.tooltip && (
        <Tooltip label={phoneNumber.tooltip.label} text={t(phoneNumber.tooltip.textKey || '')} />
      )}

      <TextField
        as={InputMask}
        mask={phoneNumber.mask}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        fashion={formHasBeenSent && validationError ? 'error' : null}
        id={inputId}
        placeholder={t(phoneNumber.placeholderKey || '')}
        onChange={handleChange}
        readOnly={submitting}
        type="tel"
        value={value}
        name="customer-phone-number"
      />

      {formHasBeenSent && validationError && (
        <GenericValidationMessage validationError={validationError} />
      )}
    </div>
  );
}
